import { getPropClassesFromInstance } from 'lib/getPropClasses';
import LayerHideOnBlur from 'components/mixins/LayerHideOnBlur';
import LayerHideOnEscape from 'components/mixins/LayerHideOnEscape';
import LayerHideOnTransition from 'components/mixins/LayerHideOnTransition';
import LayerPropTypes from './lib/LayerPropTypes';
import React from 'react';
import ReactDOM from 'react-dom';
import pickGlobalElementProps from 'utils/pickInputElementProps';

import cx from 'classnames';

import './styles/Layer.scss';

/**
 * A system of displaying layers or overlays on the page without manually positioning or the use of
 * `z-index` in CSS.
 *
 * Layer is a system for content that is rendered "above the page". This includes common elements
 * such as [Menu](/components/Menu) elements, [Tooltip](/components/Tooltip) elements,
 * [Dialog](/components/Dialog), etc. The system manages the positioning of the element on the
 * x-axis, y-axis, and its z-index. It optimizes the display of a layer by positioning it within
 * the visible viewport. For example, if you explicitly set a tooltip to be placed on the top of an
 * element that is at the top edge of the viewport when the page is scrolled down, the tooltip will
 * be displayed on the bottom of the element. The z-index of a layer is managed by simply appending
 * new layers to the end of the DOM. By simply relying on DOM ordering, only the z-index of the
 * LayerTriggers are needed to ensure all layers and nested layers (e.g., a tooltip from a menu item
 * inside of a dialog) stack properly.
 *
 * Layer itself is an unstyled component with props that are set by LayerTrigger. To create a Layer
 * component, simply compose a Layer:
 *
 * ```js
 *  import styles from './Popup.css';
 *
 *  const Popup = ({children, ...props}) => (
 *    <Layer {...props}>
 *      <div className={styles.this}>
 *        {children}
 *      </div>
 *    </Layer>
 *  );
 * ```
 *
 * All Layer components are triggered with a [LayerTrigger](/components/LayerTrigger) element.
 *
 * ```example
 *  <LayerTrigger
 *    layer={
 *      <Tooltip>
 *        Hello World!
 *      </Tooltip>
 *    }
 *    trigger="click">
 *    <Link>Click this link to show the tooltip</Link>
 *  </LayerTrigger>
 * ```
 *
 * # Configurable behaviors
 *
 * You can use the `hideOnBlur`, `hideOnEscape`, and `hideOnTransition` props to disable some of the
 * built-in behaviors of a layer.
 *
 * ```example
 *  <div>
 *    <LayerTrigger
 *      layer={
 *        <Tooltip>Hit Esc to hide this tooltip</Tooltip>
 *      }>
 *      <Link>Show the tooltip</Link>
 *    </LayerTrigger>
 *    {', '}
 *    <LayerTrigger
 *      layer={
 *        <Tooltip hideOnEscape={false}>This tooltip won’t hide when you hit Esc.</Tooltip>
 *      }>
 *      <Link>Show another tooltip</Link>
 *    </LayerTrigger>
 *  </div>
 * ```
 *
 * ## Caveats
 *
 * Layer was implemented with an older version of React before contexts existed. The approach that
 * involves calling `React.render()` on a new DOM node at the end of the document. This method was
 * considered to be creating a "portal" to a newly rendered subtree. When a version of React was
 * released with contexts enabled, this created problems since newly rendered subtrees did not have
 * the context (e.g., `react-router`, `react-intl`, `redux`, etc) of the main subtree. A hacky
 * workaround was copied from `react-router` known as `createContextWrapper()`, which copied the
 * context of `LayerTrigger` elements as well as `ModalTrigger` elements to the newly rendered
 * subtree.
 *
 * createContextWrapper() caused components to be rebuilt unnecessarily on updates, so we replaced
 * two instances of that with a different hack, `unstable_renderSubtreeIntoContainer` from ReactDOM
 * ([ref](http://stackoverflow.com/a/35837064/46040)). In the future we may want to replace our Layer
 * with [React Overlays](http://react-bootstrap.github.io/react-overlays/examples/#overlay)
 * from React Bootstrap, or just [their portals](http://react-bootstrap.github.io/react-overlays/examples/#portals).
 *
 * It should be possible to render layers within the main subtree as long as there is a top-level
 * component that wraps the entire app (e.g., [App](/components/App)). Layer state can be managed
 * in a store. For example:
 *
 * ```js
 *  import styles from './App.css';
 *
 *  export default class App extends React.Component {
 *    renderLayers() {
 *      return this.props.layers.map((layer, index) => {
 *        return React.cloneElement(layer, {
 *          key: index,
 *        })
 *      );
 *    }
 *
 *    render() {
 *      return (
 *        <div className={styles.this}>
 *          <ScrollContext>
 *            {this.props.children}
 *          </ScrollContext>
 *          {this.renderLayers()}
 *        </div>
 *      );
 *    }
 *  }
 * ```
 */

const Layer = React.createClass({
  propTypes: {
    ...LayerPropTypes,
    ...LayerHideOnBlur.propTypes,
    ...LayerHideOnEscape.propTypes,
    ...LayerHideOnTransition.propTypes,
    /** Set by LayerTrigger */
    onRequestHide: React.PropTypes.func,
    /** Set by LayerTrigger */
    position: React.PropTypes.shape({
      left: React.PropTypes.number,
      top: React.PropTypes.number,
    }),
    /** Set by LayerTrigger */
    context: React.PropTypes.object,
    /** Set by LayerTrigger */
    zIndex: React.PropTypes.number,
    /** Set by LayerTrigger */
    fixed: React.PropTypes.bool,
    /** Clicks outside the layer do not propagate */
    propagateOutsideClicks: React.PropTypes.bool,
  },

  mixins: [
    LayerHideOnBlur,
    LayerHideOnEscape,
    LayerHideOnTransition,
  ],

  statics: {
    propClasses: {
      placement: {
        bottom: 'Layer-placementBottom',
        left: 'Layer-placementLeft',
        right: 'Layer-placementRight',
        top: 'Layer-placementTop',
      },
      alignment: {
        middle: null,
        bottom: 'Layer-alignmentBottom',
        left: 'Layer-alignmentLeft',
        right: 'Layer-alignmentRight',
        top: 'Layer-alignmentTop',
      },
      fixed: 'Layer-fixed',
    },
  },

  getDefaultProps() {
    return {
      placement: 'right',
      alignment: 'middle',
      fixed: false,
      propagateOutsideClicks: true,
    };
  },

  componentDidMount() {
    if (!this.props.propagateOutsideClicks) {
      const layerContainerNode = ReactDOM.findDOMNode(this).parentNode;
      this.layerContainerParentNode = layerContainerNode.parentNode;
      this.clickLayer = document.createElement('div');
      this.clickLayer.style.cssText = 'position:fixed;top:0;width:100%;height:100%;';
      this.layerContainerParentNode.insertBefore(this.clickLayer, layerContainerNode);
    }
  },

  componentWillUnmount() {
    if (!this.props.propagateOutsideClicks) {
      this.layerContainerParentNode.removeChild(this.clickLayer);
    }
  },

  render() {
    const { style, position, zIndex, className, children, ...others } = this.props;
    const divStyle = Object.assign({}, style, position);

    if (zIndex) {
      divStyle.zIndex = zIndex;
    }

    const divProps = pickGlobalElementProps(others);
    return (
      <div
        {...divProps}
        className={cx('Layer', getPropClassesFromInstance(this), className)}
        style={divStyle}
        tabIndex={0}>
        {children}
      </div>
    );
  },
});

export default Layer;



// WEBPACK FOOTER //
// ./src/components/Layer.jsx